import {Modal} from 'antd';
import {useEffect, useRef, useState} from 'react';

import {Spinner} from '../../../layout/spinner/spinner';
import {Locale} from '../../../provider/locale/locale';
import {useEvent, useUpdateEvent} from '../../../service/events/events-hook';
import {CommonEventStatusEnum, EventFormType} from '../../../service/events/events-type';
import {addCompaniesApi} from '../../../service/feature-companies/feature-companies-api';
import {postCompanySelectorCompanySchema} from '../../../service/feature-companies/feature-companies-api-type';
import {CompaniesSelectorFeatureEnum} from '../../../service/feature-companies/feature-companies-const';
import {useCompaniesSelector} from '../../../service/feature-companies/feature-companies-hook';
import {PostSelectorCompanyType} from '../../../service/feature-companies/feature-companies-type';
import {noop} from '../../../util/function';

import {EventForm} from './event-form';
import {
    EVENT_SELECT_COMPANIES_STEP_WIDTH,
    getEventFormInitialValues,
    prepareEventRequestData,
} from './event-form-helper';

type PropsType = {
    eventId: number;
    setIsOpen: (isOpen: boolean) => void;
    onUpdateSuccess?: VoidFunction;
};

export function EditEventFormModal({eventId, setIsOpen, onUpdateSuccess}: PropsType): JSX.Element {
    const [modalWidth, setModalWidth] = useState<number>(EVENT_SELECT_COMPANIES_STEP_WIDTH);

    const {data: event, isFetching} = useEvent(eventId, true);
    const {mutateAsync: updateEvent} = useUpdateEvent(eventId);

    const companiesSelector = useCompaniesSelector<PostSelectorCompanyType>({
        feature: CompaniesSelectorFeatureEnum.Posts,
        companySchema: postCompanySelectorCompanySchema,
        resourceId: null,
        initialCompaniesCount: event?.companiesIds.length ?? 0,
    });

    const {initializeSelector, selectorId, setCompaniesSelectorFilter} = companiesSelector;

    useEffect(() => {
        initializeSelector();
    }, [initializeSelector]);

    // Seed the fresh posts selector with the event's existing companies, then reload to reflect them.
    const seededRef = useRef<boolean>(false);

    useEffect(() => {
        if (!event || !selectorId || seededRef.current) {
            return;
        }

        seededRef.current = true;

        if (event.companiesIds.length > 0) {
            addCompaniesApi({
                selectorId,
                body: {ids: event.companiesIds},
                feature: CompaniesSelectorFeatureEnum.Posts,
            })
                .then(() => setCompaniesSelectorFilter((previousFilter) => ({...previousFilter})))
                .catch(noop);
        }
    }, [event, selectorId, setCompaniesSelectorFilter]);

    async function handleSubmit(values: EventFormType, status: CommonEventStatusEnum): Promise<void> {
        await updateEvent(prepareEventRequestData(values, selectorId, status));
        onUpdateSuccess?.();
    }

    return (
        <Modal
            centered
            destroyOnClose
            footer={null}
            onCancel={() => setIsOpen(false)}
            open
            title={<Locale stringKey="EVENTS__EDIT__TITLE" />}
            width={modalWidth}
        >
            {isFetching || !event ? (
                <Spinner />
            ) : (
                <EventForm
                    closeModal={() => setIsOpen(false)}
                    companiesSelector={companiesSelector}
                    initialValues={getEventFormInitialValues(event)}
                    mode="edit"
                    onChangeStep={setModalWidth}
                    onSubmit={handleSubmit}
                />
            )}
        </Modal>
    );
}
